import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getProductSearchColumnLabel, getProductSearchRawCellValue } from "./productSearchRenderHelpers";

const escapeCsvCell = (value) => {
  const text = String(value ?? "");
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, "\"\"")}"`;
  return text;
};

const sanitizeFileNamePart = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 42);

const parseProductSearchMediaUrls = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean);
  const raw = String(value || "").trim();
  if (!raw) return [];
  return raw
    .split(/[\n\r|;,]+/)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const formatProductSearchDimensions = (row = {}) =>
  String(row?.dimensions || "").trim() ||
  [row?.listernautLength, row?.listernautWidth, row?.listernautHeight]
    .map((value) => Number(value || 0) || 0)
    .filter((value) => value > 0)
    .join(" x ");

const getProductSearchDescriptionText = (row = {}) =>
  String(row?.listernautDescription || row?.description || "").trim();

const getProductSearchSearchMediaUrls = (row = {}) => {
  const direct = parseProductSearchMediaUrls(row?.mediaUrls);
  if (direct.length) return direct;
  return parseProductSearchMediaUrls(row?.listernautMediaUrl || row?.listernautMediaUrlsRaw || row?.listernautMediaUrls);
};

const FLOOR_BIDDER_EMAIL = "floorbidder@internal.local";

const normalizeProductSearchUpc = (value = "") => String(value || "").trim().toLowerCase();

const isProductSearchPassedRow = (row = {}) => {
  const bidderName = String(row?.bidderName || "").trim().toLowerCase();
  const emailLower = String(row?.emailLower || "").trim().toLowerCase();
  const highBid = Number(row?.bid ?? row?.highBid ?? 0) || 0;
  return bidderName === "passed" || emailLower === FLOOR_BIDDER_EMAIL || highBid <= 0;
};

const formatProductSearchUpcAuctionRuns = ({ soldCount = 0, totalCount = 0 } = {}) =>
  totalCount > 0 ? `${soldCount}/${totalCount}` : "—";

export default function useProductSearch({
  orgReady,
  tab,
  apiBaseUrl,
  routes,
  apiFetchJson,
  handleNeedsAuth,
  toastOpen,
  readCache,
  writeCache,
  clearCache,
  productSearchCacheKey,
  productSearchColumnsAllowed,
  toAbsInt,
  productSearchCacheTtlMs,
  productSearchBatchSize,
  productSearchParallelBatches,
}) {
  const [productSearchAllRows, setProductSearchAllRows] = useState([]);
  const [productSearchColumns, setProductSearchColumns] = useState([]);
  const [productSearchBusy, setProductSearchBusy] = useState(false);
  const [productSearchErr, setProductSearchErr] = useState("");
  const [productSearchSearchInput, setProductSearchSearchInput] = useState("");
  const [productSearchField, setProductSearchField] = useState("all");
  const [productSearchSortKey, setProductSearchSortKey] = useState("auctionEndDate");
  const [productSearchSortDir, setProductSearchSortDir] = useState("desc");
  const [productSearchLoadedOnce, setProductSearchLoadedOnce] = useState(false);
  const [productSearchExportBusy, setProductSearchExportBusy] = useState(false);
  const [productSearchVisibleCount, setProductSearchVisibleCount] = useState(200);
  const [productSearchLoadedCount, setProductSearchLoadedCount] = useState(0);
  const [productSearchTotalCount, setProductSearchTotalCount] = useState(0);
  const [productSearchRefreshConfirmOpen, setProductSearchRefreshConfirmOpen] = useState(false);
  const productSearchContainerRef = useRef(null);

  const apiFetchJsonRef = useRef(apiFetchJson);
  const handleNeedsAuthRef = useRef(handleNeedsAuth);
  const toastOpenRef = useRef(toastOpen);
  const readCacheRef = useRef(readCache);
  const writeCacheRef = useRef(writeCache);
  const clearCacheRef = useRef(clearCache);

  useEffect(() => {
    apiFetchJsonRef.current = apiFetchJson;
  }, [apiFetchJson]);
  useEffect(() => {
    handleNeedsAuthRef.current = handleNeedsAuth;
  }, [handleNeedsAuth]);
  useEffect(() => {
    toastOpenRef.current = toastOpen;
  }, [toastOpen]);
  useEffect(() => {
    readCacheRef.current = readCache;
  }, [readCache]);
  useEffect(() => {
    writeCacheRef.current = writeCache;
  }, [writeCache]);
  useEffect(() => {
    clearCacheRef.current = clearCache;
  }, [clearCache]);

  const productSearchDisplayColumns = useMemo(() => {
    const fromApi = new Set(
      (Array.isArray(productSearchColumns) ? productSearchColumns : [])
        .map((key) => String(key || "").trim())
        .filter((key) => productSearchColumnsAllowed.includes(key))
    );
    if (!fromApi.size) return productSearchColumnsAllowed;
    // Keep frontend columns stable even if the cached/server column list is older.
    return productSearchColumnsAllowed;
  }, [productSearchColumns, productSearchColumnsAllowed]);

  const productSearchUpcDetailsByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(productSearchAllRows) ? productSearchAllRows : []).forEach((row) => {
      const upcKey = normalizeProductSearchUpc(row?.upc ?? row?.listernautUpc);
      if (!upcKey) return;
      const existing = map.get(upcKey) || {
        soldCount: 0,
        totalCount: 0,
        soldRows: [],
        passedRows: [],
      };
      existing.totalCount += 1;
      if (isProductSearchPassedRow(row)) {
        existing.passedRows.push(row);
      } else {
        existing.soldCount += 1;
        existing.soldRows.push(row);
      }
      map.set(upcKey, existing);
    });
    return map;
  }, [productSearchAllRows]);

  const productSearchAllRowsEnriched = useMemo(
    () =>
      (Array.isArray(productSearchAllRows) ? productSearchAllRows : []).map((row) => {
        const upcKey = normalizeProductSearchUpc(row?.upc ?? row?.listernautUpc);
        const upcDetails = upcKey ? productSearchUpcDetailsByKey.get(upcKey) : null;
        const retailPrice = Number(row?.retailPrice ?? row?.listernautRetailPrice ?? 0) || 0;
        const hammer = Number(row?.bid ?? row?.highBid ?? 0) || 0;
        const consignor = String(
          row?.consignor || row?.listernautConsignorName || row?.sellerCode || row?.listernautSellerCode || ""
        ).trim();
        const consignorId = String(
          row?.consignorId || row?.listernautConsignorId || row?.sellerCode || row?.listernautSellerCode || ""
        ).trim();
        return {
          ...row,
          auctionEndDate: row?.auctionEndAtMs ?? row?.auctionEndDate ?? null,
          consignor,
          consignorId,
          userType: String(row?.userType || row?.customerType || row?.derived?.customerType || "").trim(),
          bids: toAbsInt(row?.bids || 0),
          sellingToRetail: retailPrice > 0 && hammer > 0 ? hammer / retailPrice : null,
          upcAuctionRuns: formatProductSearchUpcAuctionRuns({
            soldCount: upcDetails?.soldCount || 0,
            totalCount: upcDetails?.totalCount || 0,
          }),
          upcOccurrenceSoldCount: toAbsInt(upcDetails?.soldCount || 0),
          upcOccurrenceTotalCount: toAbsInt(upcDetails?.totalCount || 0),
          upcOccurrenceDetails: {
            soldRows: Array.isArray(upcDetails?.soldRows) ? upcDetails.soldRows : [],
            passedRows: Array.isArray(upcDetails?.passedRows) ? upcDetails.passedRows : [],
          },
        };
      }),
    [productSearchAllRows, productSearchUpcDetailsByKey, toAbsInt]
  );

  const getProductSearchValueForSearch = useCallback(
    (row, key) => {
      if (key === "consignor") {
        return [row?.consignor, row?.consignorId, row?.sellerCode, row?.listernautSellerCode]
          .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
          .join(" | ");
      }
      if (key === "auctionEndDate") {
        return String(row?.auctionEndAtMs ?? row?.auctionEndDate ?? "").trim();
      }
      return getProductSearchRawCellValue(row, key, {
        getProductSearchThumbnailUrl: () => "",
        getLotDescriptionText: getProductSearchDescriptionText,
        formatLotDimensions: formatProductSearchDimensions,
        getListernautMediaUrls: getProductSearchSearchMediaUrls,
      });
    },
    []
  );

  const loadAllProductSearch = useCallback(async ({ forceRefresh = false } = {}) => {
    if (productSearchBusy) return;
    setProductSearchBusy(true);
    setProductSearchErr("");
    setProductSearchLoadedCount(0);
    setProductSearchTotalCount(0);

    try {
      if (!forceRefresh) {
        const cached = readCacheRef.current(productSearchCacheKey, { ttlMs: productSearchCacheTtlMs });
        if (cached && Array.isArray(cached?.rows)) {
          setProductSearchAllRows(cached.rows);
          setProductSearchColumns(Array.isArray(cached?.columns) ? cached.columns : []);
          setProductSearchLoadedOnce(true);
          setProductSearchVisibleCount(200);
          setProductSearchLoadedCount(cached.rows.length);
          setProductSearchTotalCount(toAbsInt(cached?.totalCount ?? cached.rows.length));
          if (productSearchContainerRef.current) productSearchContainerRef.current.scrollTop = 0;
          return;
        }
      } else {
        clearCacheRef.current(productSearchCacheKey);
      }

      const fetchProductSearchBatch = async (offset = 0, { forceRefreshBatch = false } = {}) => {
        const qs = new URLSearchParams();
        qs.set("limit", String(productSearchBatchSize));
        qs.set("offset", String(Math.max(0, Number(offset) || 0)));
        if (forceRefreshBatch) qs.set("forceRefresh", "1");
        const url = `${apiBaseUrl}${routes.lotsSearch}?${qs.toString()}`;
        const { res, data } = await apiFetchJsonRef.current(url);
        if (handleNeedsAuthRef.current(res, data)) throw new Error("Authorization required.");
        if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
        const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data?.lots) ? data.lots : [];
        const totalCount = toAbsInt(data?.totalCount ?? rows.length);
        return {
          rows,
          columns: Array.isArray(data?.columns) ? data.columns : [],
          totalCount,
          offset: Math.max(0, Number(data?.offset ?? offset) || 0),
        };
      };

      const firstBatch = await fetchProductSearchBatch(0, { forceRefreshBatch: forceRefresh });
      const allRows = [...firstBatch.rows];
      const cols = firstBatch.columns;
      const totalCount = Math.max(firstBatch.totalCount, allRows.length);
      setProductSearchLoadedCount(allRows.length);
      setProductSearchTotalCount(totalCount);

      const offsets = [];
      for (let offset = allRows.length; offset < totalCount; offset += productSearchBatchSize) {
        offsets.push(offset);
      }

      for (let index = 0; index < offsets.length; index += productSearchParallelBatches) {
        const chunk = offsets.slice(index, index + productSearchParallelBatches);
        const results = await Promise.all(chunk.map((offset) => fetchProductSearchBatch(offset)));
        results
          .sort((a, b) => a.offset - b.offset)
          .forEach((result) => {
            allRows.push(...result.rows);
          });
        setProductSearchLoadedCount(Math.min(totalCount, allRows.length));
      }

      setProductSearchAllRows(allRows);
      setProductSearchColumns(cols);
      setProductSearchLoadedOnce(true);
      setProductSearchVisibleCount(200);
      setProductSearchLoadedCount(allRows.length);
      setProductSearchTotalCount(totalCount);
      writeCacheRef.current(productSearchCacheKey, { rows: allRows, columns: cols, totalCount });
      if (productSearchContainerRef.current) productSearchContainerRef.current.scrollTop = 0;
    } catch (e) {
      setProductSearchErr(e?.message || "Failed to load products.");
      setProductSearchAllRows([]);
      setProductSearchColumns([]);
      setProductSearchLoadedCount(0);
      setProductSearchTotalCount(0);
      setProductSearchLoadedOnce(true);
    } finally {
      setProductSearchBusy(false);
    }
  }, [
    apiBaseUrl,
    routes.lotsSearch,
    productSearchBatchSize,
    productSearchBusy,
    productSearchCacheKey,
    productSearchCacheTtlMs,
    productSearchParallelBatches,
    toAbsInt,
  ]);

  const productSearchFiltered = useMemo(() => {
    const search = String(productSearchSearchInput || "").trim().toLowerCase();
    if (!search) return productSearchAllRowsEnriched;
    const field = productSearchField;
    return productSearchAllRowsEnriched.filter((row) => {
      if (field !== "all") {
        return String(getProductSearchValueForSearch(row, field) ?? "").toLowerCase().includes(search);
      }
      const haystack = [
        row?.auctionId, row?.auctionEndAtMs, row?.lot, row?.title, row?.brand, row?.condition, row?.manufacturer,
        row?.consignor, row?.consignorId, row?.sellerCode, row?.bidderName, row?.emailLower, row?.userType,
        row?.bids, row?.watches, row?.views, row?.description, row?.quantity, row?.startBid,
        row?.upcAuctionRuns, row?.retailPrice, row?.sellingToRetail, row?.upc, row?.lister,
        row?.location, row?.color, row?.weight, row?.dimensions, row?.mediaUrls, row?.bid,
        row?.listernautCondition, row?.listernautManufacturer, row?.listernautColor,
      ]
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
        .join(" | ")
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [productSearchAllRowsEnriched, productSearchSearchInput, productSearchField, getProductSearchValueForSearch]);

  const productSearchSorted = useMemo(() => {
    const sorted = [...productSearchFiltered];
    const dir = productSearchSortDir === "asc" ? 1 : -1;
    const numericColumns = new Set(["quantity", "startBid", "retailPrice", "sellingToRetail", "bid", "bids", "watches", "views"]);
    sorted.sort((a, b) => {
      if (productSearchSortKey === "auctionEndDate") {
        return (toAbsInt(a?.auctionEndAtMs || a?.auctionEndDate || 0) - toAbsInt(b?.auctionEndAtMs || b?.auctionEndDate || 0)) * dir;
      }
      if (productSearchSortKey === "lot") {
        const aValue = Number(getProductSearchValueForSearch(a, "lot"));
        const bValue = Number(getProductSearchValueForSearch(b, "lot"));
        if (Number.isFinite(aValue) && Number.isFinite(bValue)) return (aValue - bValue) * dir;
        return String(getProductSearchValueForSearch(a, "lot") || "").localeCompare(String(getProductSearchValueForSearch(b, "lot") || "")) * dir;
      }
      if (productSearchSortKey === "upcAuctionRuns") {
        const totalDiff = toAbsInt(a?.upcOccurrenceTotalCount || 0) - toAbsInt(b?.upcOccurrenceTotalCount || 0);
        if (totalDiff !== 0) return totalDiff * dir;
        const soldDiff = toAbsInt(a?.upcOccurrenceSoldCount || 0) - toAbsInt(b?.upcOccurrenceSoldCount || 0);
        if (soldDiff !== 0) return soldDiff * dir;
        return String(getProductSearchValueForSearch(a, "upc") || "").localeCompare(String(getProductSearchValueForSearch(b, "upc") || "")) * dir;
      }
      if (numericColumns.has(productSearchSortKey)) {
        return (
          (Number(getProductSearchValueForSearch(a, productSearchSortKey) || 0) || 0) -
          (Number(getProductSearchValueForSearch(b, productSearchSortKey) || 0) || 0)
        ) * dir;
      }
      return String(getProductSearchValueForSearch(a, productSearchSortKey) || "").localeCompare(
        String(getProductSearchValueForSearch(b, productSearchSortKey) || "")
      ) * dir;
    });
    return sorted;
  }, [productSearchFiltered, productSearchSortKey, productSearchSortDir, getProductSearchValueForSearch]);

  const productSearchRows = useMemo(
    () => productSearchSorted.slice(0, productSearchVisibleCount),
    [productSearchSorted, productSearchVisibleCount]
  );

  const onProductSearchScroll = useCallback((evt) => {
    const element = evt?.currentTarget;
    if (!element) return;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 140;
    if (!nearBottom) return;
    if (productSearchVisibleCount >= productSearchSorted.length) return;
    setProductSearchVisibleCount((prev) => Math.min(prev + 200, productSearchSorted.length));
  }, [productSearchVisibleCount, productSearchSorted.length]);

  const onProductSearchSort = useCallback((nextKey) => {
    const key = String(nextKey || "").trim();
    if (!key) return;
    if (productSearchSortKey === key) {
      setProductSearchSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setProductSearchSortKey(key);
      setProductSearchSortDir("asc");
    }
    setProductSearchVisibleCount(200);
  }, [productSearchSortKey]);

  const exportFilteredProductSearchCsv = useCallback(async () => {
    if (productSearchExportBusy) return;
    setProductSearchExportBusy(true);
    setProductSearchErr("");
    try {
      const rows = productSearchSorted;
      if (!rows.length) throw new Error("No rows to export.");
      const exportColumns = productSearchColumnsAllowed.filter((column) => column !== "thumbnail");
      const header = exportColumns.map((column) => escapeCsvCell(getProductSearchColumnLabel(column))).join(",");
      const lines = rows.map((row) =>
        exportColumns
          .map((column) =>
            escapeCsvCell(
              getProductSearchRawCellValue(row, column, {
                getProductSearchThumbnailUrl: () => "",
                getLotDescriptionText: getProductSearchDescriptionText,
                formatLotDimensions: formatProductSearchDimensions,
                getListernautMediaUrls: getProductSearchSearchMediaUrls,
              }) ?? ""
            )
          )
          .join(",")
      );
      const csv = "\uFEFF" + header + "\r\n" + lines.join("\r\n") + "\r\n";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const searchPart = sanitizeFileNamePart(productSearchSearchInput);
      const fileName = `product-search-${searchPart || "all"}-${stamp}.csv`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      toastOpenRef.current("CSV downloaded.", "success");
    } catch (e) {
      setProductSearchErr(e?.message || "Failed to export filtered product rows.");
      toastOpenRef.current(e?.message || "Failed to export filtered product rows.", "error");
    } finally {
      setProductSearchExportBusy(false);
    }
  }, [productSearchColumnsAllowed, productSearchExportBusy, productSearchSearchInput, productSearchSorted]);

  const openProductSearchRefreshConfirm = useCallback(() => {
    setProductSearchRefreshConfirmOpen(true);
  }, []);

  const closeProductSearchRefreshConfirm = useCallback(() => {
    setProductSearchRefreshConfirmOpen(false);
  }, []);

  const continueProductSearchRefreshAnyway = useCallback(async () => {
    setProductSearchRefreshConfirmOpen(false);
    await loadAllProductSearch({ forceRefresh: true });
  }, [loadAllProductSearch]);

  useEffect(() => {
    if (!orgReady) return;
    if (tab !== 3) return;
    if (productSearchLoadedOnce || productSearchBusy) return;
    loadAllProductSearch();
  }, [orgReady, tab, productSearchLoadedOnce, productSearchBusy, loadAllProductSearch]);

  return {
    productSearchAllRows,
    productSearchBusy,
    productSearchErr,
    productSearchSearchInput,
    setProductSearchSearchInput,
    productSearchField,
    setProductSearchField,
    productSearchSortKey,
    productSearchSortDir,
    productSearchLoadedOnce,
    productSearchExportBusy,
    productSearchVisibleCount,
    setProductSearchVisibleCount,
    productSearchLoadedCount,
    productSearchTotalCount,
    productSearchRefreshConfirmOpen,
    productSearchContainerRef,
    productSearchDisplayColumns,
    productSearchSorted,
    productSearchRows,
    loadAllProductSearch,
    onProductSearchScroll,
    onProductSearchSort,
    exportFilteredProductSearchCsv,
    openProductSearchRefreshConfirm,
    closeProductSearchRefreshConfirm,
    continueProductSearchRefreshAnyway,
  };
}
